import React, { useEffect, useState } from 'react';
import axios from 'axios';

const REFRESH_INTERVAL_MS = 30000;

// Tiny, dependency-light fetcher — we deliberately don't use the auth-aware
// apiClient because this endpoint is public and we don't want CORS preflight
// or cookies on the marketing landing page.
const fetchLiveCount = async () => {
  const base = process.env.REACT_APP_BACKEND_URL;
  const res = await axios.get(`${base}/api/public/live-count`, {
    withCredentials: false,
  });
  return res.data;
};

const LiveOnlineBadge = () => {
  const [data, setData] = useState(null); // { online, nearby, city }

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const d = await fetchLiveCount();
        if (!cancelled && d && typeof d === 'object') setData(d);
      } catch (err) {
        // Public widget — silently skip on transient failure.
        console.debug('Live-count fetch failed:', err?.message);
      }
    };
    tick();
    const id = setInterval(tick, REFRESH_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!data) return null;

  // Prefer the city-aware variant — much more compelling social proof —
  // but only when we have a real `city` AND the nearby count is meaningful.
  const useNearby = data.city && typeof data.nearby === 'number' && data.nearby >= 4;
  const count = useNearby ? data.nearby : data.online;
  const tail = useNearby ? `singles online in ${data.city}` : 'singles online right now';

  return (
    <div
      className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 shadow-lg shadow-black/20 mb-4"
      data-testid="live-online-badge"
      role="status"
      aria-live="polite"
    >
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70"></span>
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400 shadow-[0_0_8px_rgba(74,222,128,0.9)]"></span>
      </span>
      <span className="text-white text-xs sm:text-sm font-semibold tracking-wide">
        <span className="text-emerald-300 font-bold" data-testid="live-online-count">{count}</span>
        <span className="text-white/85 ml-1" data-testid="live-online-tail">{tail}</span>
      </span>
    </div>
  );
};

export default LiveOnlineBadge;
